import { useVirtualizer } from '@tanstack/react-virtual';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useDebounceCallback } from 'usehooks-ts';
import { InputAutoCompleteSimpleI } from './input-autocomplete-simple.interface';
import InputAutocompleteSimpleLayout from '../input-autocomplete-simple-layout/input-autocomplete-simple-layout.component';
import { MIN_OPTION_HEIGHT, MIN_OPTION_VISIBLE } from '../input-autocomplete/input-autocomplete.constants';
import {
    filterQuerySimple,
    handleAutocompleteRemoteQuery,
    renderOptionSimple,
} from '../input-autocomplete/input-autocomplete.utils';

export const InputAutoCompleteSimple = ({
    name,
    label,
    testId,
    value,
    options,
    isLoading = false,
    minVisible = MIN_OPTION_VISIBLE,
    optionDropdownClasses,
    optionClasses,
    containerClasses,
    enableEmptyOption = false,
    emptyOptionLabel,
    debounceTime = 3000,
    filterRemote,
    itemNotFoundError = false,
    itemNotFoundErrorMessage,
    position,
    onChange,
    onInputChange,
    onScrollToEnd,
    callback,
    required,
    onClick,
    onFocus,
    ...props
}: InputAutoCompleteSimpleI) => {
    const modalRef = useRef<HTMLDivElement | null>(null);
    const calledQuery = useRef<string>('');
    // options change control
    const optionLengthControl = useRef<{ continue: boolean; length: number }>({
        continue: true,
        length: options.length,
    });

    const [show, setShow] = useState(false);
    const [query, setQuery] = useState<string>(value ?? '');
    const [valueSelected, setValueSelected] = useState<string | undefined>(value);

    // Debounce query
    const debounced = useDebounceCallback(
        (value) => {
            if (value.length && options.length) {
                callback && callback(value);
            }
        },
        debounceTime,
        { leading: true },
    );

    // Options handling empty option
    const filteredOptions = useMemo(() => {
        const optionsWithEmpty = enableEmptyOption ? ['', ...options] : options;

        return query !== valueSelected ? filterQuerySimple(optionsWithEmpty, query) : optionsWithEmpty;
    }, [enableEmptyOption, options, query, valueSelected]);

    const rowVirtualizer = useVirtualizer({
        count: filteredOptions.length,
        getScrollElement: () => modalRef.current,
        estimateSize: () => MIN_OPTION_HEIGHT,
    });

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newText = event.target.value;
        setQuery(newText);
        onInputChange && onInputChange(newText);
    };

    const handleSelect = (value: string) => {
        setValueSelected(value);
        setQuery(value);
        setShow(false);
        onChange && onChange(value);
    };

    // handle scroll end
    useEffect(() => {
        const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

        if (!lastItem) {
            return;
        }

        const isAtEndOfList = lastItem.index === filteredOptions.length - 1;
        const optionLengthHasChanged = optionLengthControl.current.continue;
        const filteredGotQuery = query
            ? filteredOptions.some((item) => item.toLocaleLowerCase().includes(query.toLocaleLowerCase()))
            : false;

        if (isAtEndOfList && !isLoading && optionLengthHasChanged && !filteredGotQuery) {
            onScrollToEnd && onScrollToEnd();
            optionLengthControl.current = {
                ...optionLengthControl.current,
                continue: false,
            };
        } else if (!optionLengthControl.current.continue && optionLengthControl.current.length < options.length) {
            optionLengthControl.current = {
                continue: true,
                length: options.length,
            };
        }
        // Do NOT delete eslint bypass, change rowVirtualizer.getVirtualItems() to rowVirtualizer
        // it makes useeffect doesnt work properly
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        // eslint-disable-next-line react-hooks/exhaustive-deps
        rowVirtualizer.getVirtualItems(),
        filteredOptions,
        options,
        onScrollToEnd,
        isLoading,
    ]);

    // handle trigger callback
    useEffect(() => {
        handleAutocompleteRemoteQuery({
            query,
            filteredOptionsLength: filteredOptions.length,
            isLoading,
            filterRemote,
            calledQueryRef: calledQuery,
            shouldOpenDropdown: filteredOptions.length > 0 && !show && query.length > 0 && valueSelected !== query,
            openDropdown: () => setShow(true),
            debounced,
        });
    }, [debounced, filterRemote, filteredOptions, isLoading, query, show, valueSelected]);

    const optionsContainerClasses = twMerge(
        position === 'top'
            ? 'overflow-auto w-full shadow-lg border border-gray-50 rounded-t-lg bg-white absolute z-50 bottom-full mb-1'
            : 'overflow-auto w-full shadow-lg border border-gray-50 rounded-b-lg bg-white relative z-50',
        show && filteredOptions.length > 0 && 'py-2',
        optionDropdownClasses,
    );
    const optionItemClasses = twMerge(
        'flex justify-start items-center pl-2 font-light hover:bg-gray-50',
        optionClasses,
    );

    return (
        <InputAutocompleteSimpleLayout
            name={name}
            label={label}
            ref={modalRef}
            show={show}
            query={query}
            testId={testId || `${name}-autocomplete`}
            placeholder={emptyOptionLabel || props.placeholder}
            isLoading={isLoading}
            minVisible={minVisible}
            optionsLength={filteredOptions.length}
            optionsHeight={rowVirtualizer.getTotalSize()}
            handleShow={(value?: boolean) => setShow(value ?? ((prevState) => !prevState))}
            handleInputChange={handleInputChange}
            containerClasses={containerClasses}
            optionsContainerClasses={optionsContainerClasses}
            error={itemNotFoundError}
            errorMessage={itemNotFoundError ? itemNotFoundErrorMessage : ''}
            required={required}
            onClick={onClick}
            onFocus={onFocus}
            {...props}
        >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                return renderOptionSimple({
                    name,
                    item: filteredOptions[virtualRow.index] as string,
                    isSelected: filteredOptions[virtualRow.index] === valueSelected,
                    style: {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                    },
                    classes: optionItemClasses,
                    action: handleSelect,
                    disabled: false,
                });
            })}
        </InputAutocompleteSimpleLayout>
    );
};

export default InputAutoCompleteSimple;
